import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { InterviewCard, OperatorCard, LoadingBlock } from "@/components/site/Cards";

export default function TopicDetail() {
  const { slug } = useParams();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/topics/${slug}`).then((r) => setT(r.data)).finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!t) return;
    // Emit structured data (JSON-LD) for SEO and AI search grounding.
    const script = document.createElement("script");
    script.type = "application/ld+json";
    const faq = (t.key_questions || []).slice(0, 6).map((q) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: {
        "@type": "Answer",
        text:
          (t.why_it_matters ? `${t.why_it_matters} ` : "") +
          (t.definition || "") +
          " Explored through operator interviews on Operating Class.",
      },
    }));
    const payload = [
      {
        "@context": "https://schema.org",
        "@type": "DefinedTerm",
        name: t.name,
        description: t.definition || t.description || "",
        inDefinedTermSet: "https://operatingclass.com/topics",
        url: `https://operatingclass.com/topics/${t.slug}`,
      },
    ];
    if (faq.length > 0) {
      payload.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq,
      });
    }
    script.text = JSON.stringify(payload);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [t]);

  if (loading) return <LoadingBlock />;
  if (!t) return <div className="p-20 text-center">Topic not found.</div>;

  return (
    <div data-testid="topic-detail">
      <section className="border-b border-oc-hairline bg-oc-sand">
        <div className="max-w-editorial mx-auto px-6 md:px-10 py-16 md:py-24">
          <Link to="/topics" className="inline-flex items-center gap-2 font-ui text-xs font-bold uppercase tracking-widest text-oc-ink hover:text-oc-blue mb-8">
            <ArrowLeft className="w-3.5 h-3.5" /> All problems
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-8">
              <div className="eyebrow text-oc-coral mb-6">The problem</div>
              <h1 className="hero-headline text-5xl md:text-7xl text-oc-ink">{t.name}</h1>
              {t.definition && (
                <p className="mt-6 font-editorial text-xl md:text-2xl text-oc-ink-soft leading-snug">
                  {t.definition}
                </p>
              )}
              {t.description && (
                <p className="mt-6 font-editorial text-lg text-oc-ink leading-relaxed max-w-2xl">
                  {t.description}
                </p>
              )}
            </div>
            {t.why_it_matters && (
              <aside className="lg:col-span-4">
                <div className="border-l-2 border-oc-coral pl-6 py-2">
                  <div className="eyebrow text-oc-ink-muted mb-3">Why it matters</div>
                  <p className="font-editorial text-lg text-oc-ink leading-snug">
                    {t.why_it_matters}
                  </p>
                </div>
              </aside>
            )}
          </div>
        </div>
      </section>

      {(t.key_questions?.length > 0 || t.signals?.length > 0 || t.starting_points?.length > 0) && (
        <section className="border-b border-oc-hairline bg-oc-beige">
          <div className="max-w-editorial mx-auto px-6 md:px-10 py-20">
            <div className="eyebrow text-oc-coral mb-4">The Operating Lens</div>
            <h2 className="font-editorial text-3xl md:text-4xl mb-14 max-w-2xl leading-tight">
              How exceptional operators approach {t.name.toLowerCase()}.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
              {t.key_questions?.length > 0 && (
                <LensBlock
                  eyebrow="Questions worth sitting with"
                  items={t.key_questions}
                  accent="oc-blue"
                />
              )}
              {t.signals?.length > 0 && (
                <LensBlock
                  eyebrow="Signals you may be here"
                  items={t.signals}
                  accent="oc-coral"
                />
              )}
              {t.starting_points?.length > 0 && (
                <LensBlock
                  eyebrow="Practical starting points"
                  items={t.starting_points}
                  accent="oc-ink"
                />
              )}
            </div>
          </div>
        </section>
      )}

      {t.interviews?.length > 0 && (
        <section className="border-b border-oc-hairline">
          <div className="max-w-editorial mx-auto px-6 md:px-10 py-16 md:py-20">
            <div className="eyebrow text-oc-coral mb-4">Featured Interviews</div>
            <h2 className="font-editorial text-3xl md:text-4xl mb-12">Conversations on {t.name.toLowerCase()}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {t.interviews.map((iv) => <InterviewCard key={iv.slug} interview={iv} />)}
            </div>
          </div>
        </section>
      )}

      {t.operators?.length > 0 && (
        <section className="bg-oc-sand">
          <div className="max-w-editorial mx-auto px-6 md:px-10 py-16 md:py-20">
            <div className="eyebrow text-oc-coral mb-4">Featured contributors</div>
            <h2 className="font-editorial text-3xl md:text-4xl mb-12">Operators known for {t.name.toLowerCase()}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {t.operators.map((op) => <OperatorCard key={op.slug} operator={op} />)}
            </div>
          </div>
        </section>
      )}

      {/* KEEP READING / NEXT PROBLEM */}
      <section className="border-t border-oc-hairline bg-oc-beige">
        <div className="max-w-editorial mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <div className="eyebrow text-oc-coral mb-4">Keep reading</div>
              <h3 className="font-editorial text-3xl md:text-4xl leading-tight text-oc-ink">
                Every article and interview on this problem.
              </h3>
              <Link
                to={`/read?topic=${t.slug}`}
                className="mt-6 inline-flex items-center gap-2 font-ui text-xs font-bold uppercase tracking-widest bg-oc-ink text-white px-6 h-12 hover:bg-oc-blue transition-colors"
              >
                See the archive on {t.name.toLowerCase()} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div>
              <div className="eyebrow text-oc-coral mb-4">Or pick a different problem</div>
              <h3 className="font-editorial text-3xl md:text-4xl leading-tight text-oc-ink">
                All the operating problems we cover.
              </h3>
              <Link
                to="/topics"
                className="mt-6 inline-flex items-center gap-2 font-ui text-xs font-bold uppercase tracking-widest border border-oc-ink px-6 h-12 hover:bg-oc-ink hover:text-white transition-colors"
              >
                Browse all problems <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function LensBlock({ eyebrow, items, accent }) {
  const border = accent === "oc-coral" ? "border-oc-coral" : accent === "oc-blue" ? "border-oc-blue" : "border-oc-ink";
  const num = accent === "oc-coral" ? "text-oc-coral" : accent === "oc-blue" ? "text-oc-blue" : "text-oc-ink";
  return (
    <div>
      <div className={`eyebrow text-oc-ink-muted mb-6 pb-3 border-b-2 ${border}`}>{eyebrow}</div>
      <ol className="space-y-5">
        {items.map((it, i) => (
          <li key={i} className="flex gap-4">
            <span className={`font-ui font-bold text-xs ${num} mt-1.5 w-7 flex-shrink-0`}>{String(i + 1).padStart(2, "0")}</span>
            <span className="font-editorial text-lg leading-snug text-oc-ink">{it}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}
